import { memo, useCallback, useState } from 'react';

import { WorkspaceAgentDescribeControl } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/form/WorkspaceAgentDescribeControl';
import { WorkspaceAgentPanelConsentDock } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/WorkspaceAgentPanelConsentDock';
import { PrivateWorkspaceAgentMonikerText } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/read/PrivateWorkspaceAgentMonikerText';
import { DESCRIBE_TEXT_MAX_LENGTH } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentFormInput';
import { useTrackWorkspaceAgentFormChoice } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useTrackWorkspaceAgentFormChoice';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type DescribeElicitation } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

export type PrivateWorkspaceAgentPanelDescribeDockProps = {
  elicitation: DescribeElicitation;
};

export const PrivateWorkspaceAgentPanelDescribeDock = memo(
  ({ elicitation }: PrivateWorkspaceAgentPanelDescribeDockProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const trackChoice = useTrackWorkspaceAgentFormChoice(elicitation);
    const [text, setText] = useState('');
    const trimmed = text.trim();
    const canSubmit = trimmed.length > 0 && trimmed.length <= DESCRIBE_TEXT_MAX_LENGTH;

    const handleContinue = useCallback(() => {
      if (!canSubmit) {
        return;
      }

      trackChoice({ cta: 'approve' });
      void session.respondToElicitation({
        customText: trimmed,
        elicitationId: elicitation.id,
        response: 'approved',
      });
    }, [canSubmit, elicitation.id, session, trackChoice, trimmed]);

    const handleCancel = useCallback(() => {
      trackChoice({ cta: 'cancel' });
      void session.respondToElicitation({
        elicitationId: elicitation.id,
        response: 'denied',
      });
    }, [elicitation.id, session, trackChoice]);

    return (
      <WorkspaceAgentPanelConsentDock
        title={<PrivateWorkspaceAgentMonikerText value={elicitation.payload.prompt} />}
        approveLabel="Continue"
        data-testid="WORKSPACE_AGENT_DESCRIBE"
        isApproveDisabled={!canSubmit}
        onApprove={handleContinue}
        onCancel={handleCancel}
      >
        <WorkspaceAgentDescribeControl onChange={setText} placeholder={elicitation.payload.placeholder} value={text} />
      </WorkspaceAgentPanelConsentDock>
    );
  },
);

PrivateWorkspaceAgentPanelDescribeDock.displayName = 'PrivateWorkspaceAgentPanelDescribeDock';
